import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, Smartphone } from "lucide-react";
import { API, errorText } from "@/lib/api";

// Mobile number + SMS OTP. Calls onChange({ phone, token }) once verified, or onChange(null) when the number changes.
// A verified number is remembered for the rest of the visit (the server pass lasts an hour), so people verify once.
const KEY = "aor_phone_pass";
const pretty = (p) => p.replace(/^\+91(\d{5})(\d{5})$/, "+91 $1 $2");
const tenDigits = (p) => { let d = p.replace(/\D/g, ""); if (d.length === 12 && d.startsWith("91")) d = d.slice(2); else if (d.length === 11 && d.startsWith("0")) d = d.slice(1); return /^[6-9]\d{9}$/.test(d) ? d : null; };

// Which SMS provider the server has switched on ("firebase", "2factor", "dev" or null), fetched once per page load.
let cfgP;
const otpConfig = () => (cfgP ||= axios.get(`${API}/otp/config`).then((r) => r.data).catch(() => ({ provider: null })));
let fb;
const firebaseAuth = async (cfg) => {
  if (!fb) {
    const [{ initializeApp }, mod] = await Promise.all([import("firebase/app"), import("firebase/auth")]);
    fb = { auth: mod.getAuth(initializeApp(cfg)), mod };
  }
  return fb;
};
const FB_ERRORS = {
  "auth/invalid-phone-number": "Enter a valid 10-digit Indian mobile number.",
  "auth/too-many-requests": "Too many attempts. Please wait a few minutes and try again.",
  "auth/quota-exceeded": "We can't send codes right now. Please try again later.",
  "auth/invalid-verification-code": "That code doesn't match. Check the SMS and try again.",
  "auth/code-expired": "This code has expired. Request a new one.",
  "auth/captcha-check-failed": "The security check failed. Please reload the page and try again.",
};
const fbError = (e) => FB_ERRORS[e?.code] || errorText(e);

export const savedPhone = () => {
  try {
    const v = JSON.parse(sessionStorage.getItem(KEY) || "null");
    return v && v.exp > Date.now() ? v : null;
  } catch { return null; }
};

export function PhoneVerify({ value, onChange, dark = false, testId = "phone" }) {
  const [phone, setPhone] = useState("");
  const [req, setReq] = useState(null);       // { request_id, phone, dev }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [wait, setWait] = useState(0);
  const codeRef = useRef(null);
  const boxRef = useRef(null);      // invisible reCAPTCHA host for Firebase
  const confirmRef = useRef(null);  // Firebase confirmation for the code just sent

  useEffect(() => { if (!value) { const s = savedPhone(); if (s) onChange({ phone: s.phone, token: s.token }); } }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (wait <= 0) return undefined; const t = setTimeout(() => setWait(wait - 1), 1000); return () => clearTimeout(t); }, [wait]);

  const label = dark ? "text-slate-200" : "text-slate-700";
  const input = `h-12 w-full rounded-lg border px-3 text-[15px] outline-none ${dark ? "border-white/20 bg-white/10 text-white placeholder:text-slate-400 focus:border-teal-300" : "border-slate-300 bg-white focus:border-[#0E9384] focus:ring-2 focus:ring-teal-100"}`;
  const btn = "btn-teal shrink-0 rounded-lg px-4 text-[14px] font-semibold disabled:opacity-60";

  const send = async () => {
    setErr("");
    const d = tenDigits(phone);
    if (!d) return setErr("Enter a valid 10-digit Indian mobile number.");
    setBusy(true);
    try {
      const cfg = await otpConfig();
      if (cfg.provider === "firebase") {
        const { auth, mod } = await firebaseAuth(cfg.firebase);
        window.__aorRecaptcha?.clear?.();
        boxRef.current.innerHTML = "";
        const host = document.createElement("div");
        boxRef.current.appendChild(host);
        window.__aorRecaptcha = new mod.RecaptchaVerifier(auth, host, { size: "invisible" });
        confirmRef.current = await mod.signInWithPhoneNumber(auth, `+91${d}`, window.__aorRecaptcha);
        setReq({ phone: `+91${d}`, firebase: true });
      } else {
        const { data } = await axios.post(`${API}/otp/send`, { phone: d });
        setReq(data);
      }
      setCode(""); setWait(30);
      setTimeout(() => codeRef.current?.focus(), 50);
    } catch (e) { setErr(fbError(e)); } finally { setBusy(false); }
  };
  const verify = async () => {
    setErr(""); setBusy(true);
    try {
      let data;
      if (req.firebase) {
        const cred = await confirmRef.current.confirm(code);
        const idToken = await cred.user.getIdToken();
        ({ data } = await axios.post(`${API}/otp/firebase`, { id_token: idToken }));
        fb.mod.signOut(fb.auth).catch(() => {});  // we only needed proof of the number, not a Firebase session
      } else {
        ({ data } = await axios.post(`${API}/otp/verify`, { request_id: req.request_id, code }));
      }
      const pass = { phone: data.phone, token: data.phone_token };
      try { sessionStorage.setItem(KEY, JSON.stringify({ ...pass, exp: Date.now() + (data.valid_minutes - 5) * 60000 })); } catch { /* private mode */ }
      setReq(null); onChange(pass);
    } catch (e) { setErr(fbError(e)); } finally { setBusy(false); }
  };
  const reset = () => { try { sessionStorage.removeItem(KEY); } catch { /* ignore */ } setReq(null); setCode(""); setErr(""); onChange(null); };

  if (value) return (
    <div data-testid={`${testId}-verified`} className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 ${dark ? "border-teal-300/40 bg-teal-300/10" : "border-teal-200 bg-teal-50"}`}>
      <span className={`flex items-center gap-2 text-[14.5px] font-semibold ${dark ? "text-teal-200" : "text-teal-800"}`}><CheckCircle2 className="h-4 w-4" />{pretty(value.phone)} verified</span>
      <button type="button" onClick={reset} className={`text-[13px] font-semibold underline ${dark ? "text-slate-300" : "text-slate-600"}`}>Change</button>
    </div>
  );

  return (
    <div className="space-y-2">
      <span className={`flex items-center gap-1.5 text-[13px] font-semibold ${label}`}><Smartphone className="h-3.5 w-3.5" /> Mobile number *</span>
      {!req ? (
        <div className="flex gap-2">
          <input data-testid={`${testId}-input`} type="tel" inputMode="numeric" autoComplete="tel-national" placeholder="10-digit mobile number" value={phone}
            onChange={(e) => setPhone(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (phone.replace(/\D/g, "").length >= 10) send(); } }} className={input} />
          <button type="button" data-testid={`${testId}-send`} onClick={send} disabled={busy || phone.replace(/\D/g, "").length < 10} className={btn}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send OTP"}</button>
        </div>
      ) : (
        <>
          <p className={`text-[13px] ${dark ? "text-slate-300" : "text-slate-600"}`}>Enter the code sent by SMS to <b>{pretty(req.phone)}</b>.{" "}
            <button type="button" onClick={() => { setReq(null); setErr(""); }} className="font-semibold underline">Change number</button></p>
          <div className="flex gap-2">
            <input ref={codeRef} data-testid={`${testId}-code`} inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="6-digit code" value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (code.length >= 4) verify(); } }} className={`${input} tracking-[0.3em]`} />
            <button type="button" data-testid={`${testId}-verify`} onClick={verify} disabled={busy || code.length < 4} className={btn}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}</button>
          </div>
          <p className={`text-[12.5px] ${dark ? "text-slate-400" : "text-slate-500"}`}>
            {wait > 0 ? `Didn't get it? Resend in ${wait}s` : <button type="button" onClick={send} disabled={busy} className="font-semibold underline">Resend code</button>}
            {req.dev && " · Test mode: the code is in the server log"}</p>
        </>
      )}
      <div ref={boxRef} />
      {err && <p data-testid={`${testId}-error`} className={`text-[13px] font-medium ${dark ? "text-red-300" : "text-red-600"}`}>{err}</p>}
    </div>
  );
}
